import { useState } from "react";
import { motion } from "framer-motion";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useModal } from "../../contexts/ModalContext";
import { useLanguage } from "../../contexts/LanguageContext";

const fieldAnim = (i) => ({ initial: { opacity: 0, y: 18 }, animate: { opacity: 1, y: 0 }, transition: { delay: 0.08 * i, duration: 0.45 } });

function SignupForm() {
  const [passwordStrength, setPasswordStrength] = useState({ strength: 0, text: "", barWidth: "0%", color: "#f87171" });
  const [phone, setPhone] = useState("");
  const [transcripts, setTranscripts] = useState([]);
  const [selectedOption, setSelectedOption] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showModal } = useModal();
  const { t } = useLanguage();

  const handlePasswordChange = (event) => {
    const password = event.target.value;
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.match(/[a-z]/) && password.match(/[A-Z]/)) strength++;
    if (password.match(/\d/)) strength++;
    if (password.match(/[^a-zA-Z\d]/)) strength++;

    const map = {
      0: { text: t("weak"), barWidth: "25%", color: "#f87171" },
      1: { text: t("weak"), barWidth: "25%", color: "#f87171" },
      2: { text: t("fair"), barWidth: "50%", color: "#fbbf24" },
      3: { text: t("strong"), barWidth: "75%", color: "#34d399" },
      4: { text: t("veryStrong"), barWidth: "100%", color: "#10b981" },
    };
    setPasswordStrength({ strength, ...map[strength] });
  };

  const handleTranscriptUpload = (event) => {
    const files = Array.from(event.target.files || []);
    const updated = [...transcripts];
    files.forEach((file) => {
      if (updated.length < 5) updated.push(file.name || `Transcript ${updated.length + 1}`);
    });
    setTranscripts(updated.slice(0, 5));
  };

  const removeTranscript = (index) => {
    const updated = [...transcripts];
    updated.splice(index, 1);
    setTranscripts(updated);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!selectedOption) { showModal(t("selectProfileValidation"), t("validationTitle")); return; }
    setIsSubmitting(true);
    setTimeout(() => {
      showModal(t("signupReceived"), t("signupModalTitle"));
      setIsSubmitting(false);
      event.target.reset();
      setPhone(""); setSelectedOption(""); setTranscripts([]);
      setPasswordStrength({ strength: 0, text: "", barWidth: "0%", color: "#f87171" });
    }, 400);
  };

  const labelStyle = { display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-secondary)' };
  const inputStyle = {
    width: '100%', padding: '10px 14px', background: 'var(--input-bg)', border: '1px solid var(--input-border)',
    borderRadius: '10px', color: 'var(--text-primary)', fontSize: '0.9rem', outline: 'none', transition: 'border-color 0.2s, background 0.2s',
  };

  return (
    <motion.form
      id="professional-signup-form"
      onSubmit={handleSubmit}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      style={{
        display: 'grid', gap: '20px', maxWidth: '560px', margin: '40px auto', padding: '36px',
        background: 'var(--bg-card)', backdropFilter: 'var(--card-backdrop)', WebkitBackdropFilter: 'var(--card-backdrop)',
        border: '1px solid var(--border-glass)', borderRadius: '20px', boxShadow: 'var(--shadow-lg)',
        position: 'relative', overflow: 'hidden',
      }}
    >
      {/* Accent bar */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, #10b981, #7c3aed)' }} />

      <div style={{ textAlign: 'center', marginBottom: '8px' }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', background: 'linear-gradient(135deg, #10b981, #7c3aed)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{t("professionalSignUpTitle")}</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '4px' }}>{t("professionalJoinText")}</p>
      </div>

      <motion.div {...fieldAnim(0)}>
        <label style={labelStyle}>{t("fullNameLabel")}</label>
        <input type="text" name="name" required placeholder={t("fullNamePlaceholder")} style={inputStyle} onFocus={e => { e.target.style.borderColor = 'rgba(var(--accent-primary-rgb), 0.6)'; e.target.style.background = 'var(--input-focus-bg)'; }} onBlur={e => { e.target.style.borderColor = 'var(--input-border)'; e.target.style.background = 'var(--input-bg)'; }} />
      </motion.div>

      <motion.div {...fieldAnim(1)}>
        <label style={labelStyle}>{t("emailAddressRequired")}</label>
        <input type="email" name="email" required placeholder={t("emailAddress")} style={inputStyle} onFocus={e => { e.target.style.borderColor = 'rgba(var(--accent-primary-rgb), 0.6)'; e.target.style.background = 'var(--input-focus-bg)'; }} onBlur={e => { e.target.style.borderColor = 'var(--input-border)'; e.target.style.background = 'var(--input-bg)'; }} />
      </motion.div>

      <motion.div {...fieldAnim(2)}>
        <label style={labelStyle}>{t("password")} *</label>
        <input type="password" name="password" required placeholder={t("createSecurePassword")} style={inputStyle} onChange={handlePasswordChange} onFocus={e => { e.target.style.borderColor = 'rgba(var(--accent-primary-rgb), 0.6)'; e.target.style.background = 'var(--input-focus-bg)'; }} onBlur={e => { e.target.style.borderColor = 'var(--input-border)'; e.target.style.background = 'var(--input-bg)'; }} />
        <div style={{ height: '4px', marginTop: '8px', background: 'var(--bg-elevated)', borderRadius: '4px', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: passwordStrength.barWidth, background: passwordStrength.color, borderRadius: '4px', transition: 'width 0.3s, background 0.3s' }} />
        </div>
        {passwordStrength.text && <span style={{ fontSize: '0.75rem', color: passwordStrength.color, marginTop: '2px' }}>{passwordStrength.text}</span>}
      </motion.div>

      <motion.div {...fieldAnim(3)}>
        <label style={labelStyle}>{t("phoneNumberLabel")}</label>
        <PhoneInput country={"us"} value={phone} onChange={(value) => setPhone(value)} enableAreaCodes enableLongNumbers
          inputProps={{ name: "phoneNumber", required: true }}
          containerStyle={{ display: "flex", alignItems: "center" }}
          inputStyle={{ width: "100%", paddingLeft: "50px", background: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '10px', color: 'var(--text-primary)' }}
          buttonStyle={{ position: "absolute", left: "10px", zIndex: 1, background: 'transparent', border: 'none' }}
        />
      </motion.div>

      <motion.div {...fieldAnim(4)}>
        <label style={labelStyle}>{t("transcriptUploadLabel")}</label>
        <div style={{ border: '2px dashed var(--border-glass)', padding: '24px', textAlign: 'center', borderRadius: '14px', background: 'var(--bg-elevated)', transition: 'border-color 0.2s' }}>
          <input type="file" name="transcripts" accept=".jpg,image/jpeg" multiple onChange={handleTranscriptUpload} style={{ width: '100%', color: 'var(--text-secondary)' }} />
          <p style={{ marginTop: '8px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>{t("dragDropHint")}</p>
        </div>
        {transcripts.length > 0 && (
          <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {transcripts.map((t, i) => (
              <div key={i} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', background: 'var(--bg-glass)', border: '1px solid var(--border-glass)', borderRadius: '8px', padding: '4px 10px', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                {t}
                <button type="button" onClick={() => removeTranscript(i)} style={{ marginLeft: '6px', background: 'rgba(248,113,113,0.2)', border: 'none', color: '#f87171', borderRadius: '50%', width: '18px', height: '18px', cursor: 'pointer', fontSize: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
            ))}
          </div>
        )}
      </motion.div>

      <motion.div {...fieldAnim(5)}>
        <label style={labelStyle}>{t("personalImageUploadLabel")}</label>
        <div style={{ border: '2px dashed var(--border-glass)', padding: '24px', textAlign: 'center', borderRadius: '14px', background: 'var(--bg-elevated)' }}>
          <input type="file" name="personalImage" accept=".jpg,image/jpeg" required style={{ width: '100%', color: 'var(--text-secondary)' }} />
        </div>
      </motion.div>

      <motion.div {...fieldAnim(6)}>
        <label style={labelStyle}>{t("chooseProfileDesignLabel")}</label>
        <div style={{ display: 'flex', gap: '16px' }}>
          {['1', '2'].map(v => (
            <label key={v} style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px',
              borderRadius: '12px', cursor: 'pointer', transition: 'all 0.2s',
              background: selectedOption === v ? 'rgba(16,185,129,0.1)' : 'var(--bg-elevated)',
              border: selectedOption === v ? '2px solid #10b981' : '2px solid var(--border-subtle)',
              color: selectedOption === v ? '#10b981' : 'var(--text-secondary)', fontWeight: 600, fontSize: '0.85rem',
            }}>
              <input type="radio" name="profileOption" value={v} checked={selectedOption === v} onChange={(e) => setSelectedOption(e.target.value)} style={{ accentColor: '#10b981' }} />
              {t("profileOption")} {v}
            </label>
          ))}
        </div>
      </motion.div>

      <motion.button {...fieldAnim(7)} type="submit" disabled={isSubmitting} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
        style={{
          padding: '14px', border: 'none', borderRadius: '12px', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer',
          background: 'linear-gradient(135deg, #10b981, #7c3aed)', color: '#fff', transition: 'opacity 0.2s',
          opacity: isSubmitting ? 0.6 : 1,
        }}>
        {isSubmitting ? t("submitting") : t("signUpProfessionalBtn")}
      </motion.button>
    </motion.form>
  );
}

export default SignupForm;
